import { forwardRef, memo, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { setAlpha } from '../../design-tokens';
import { useClosestAttribute } from '../../hooks/useClosestAttribute';
import useForceUpdate from '../../hooks/useForceUpdate';
import { useIsRTL } from '../../locale';
import { useLanguage } from '../../locale/useTextLocalizer';
import { renderDesignTokens, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { useIconA11yInfo } from '../Icon/useIconA11yInfo';
import { findLocalizedSVG } from './__internal__/findLocalizedSVG';
import { getSVG, isSVGAvailableInCache, updateSVG } from './__internal__/SVGCache';
import '@sas/react-svg/style/filament-assets.css';
import cn from './SVG.css.json';
import './SVG.css';
import { jsx as _jsx } from "react/jsx-runtime";
const extractSVGCustomProps = props => {
  const {
    icon,
    tooltip,
    type,
    ariaLabel,
    ariaHidden,
    testId,
    ariaLabelledBy,
    ariaDescribedBy,
    disabled,
    className,
    style,
    theme,
    ...htmlProps
  } = props;

  // NOTE: the difference between this mapped type and Required<CustomSVGProps> is that
  //     | the value types still have undefined included (they would be removed with Required).
  // NOTE: This mapped type guarantees that any new additions to CustomSVGProps fail a type check
  //     | instead of causing large console.error logs at runtime in DEV mode.
  const customProps = {
    // WARNING: do not refer to htmlProps in this, add new props to the de-structured list above instead.
    icon,
    tooltip,
    type,
    ariaLabel,
    ariaHidden,
    testId,
    ariaLabelledBy,
    ariaDescribedBy,
    disabled,
    theme,
    className,
    style
  };
  return [customProps, htmlProps];
};
export const SVG = /*#__PURE__*/memo( /*#__PURE__*/forwardRef((props, ref) => {
  const [{
    testId,
    type,
    icon
  }, {
    role,
    ...restProps
  }] = extractSVGCustomProps(props);
  const [localizedIcon, setLocalizedIcon] = useState(undefined);
  const dt = useDesignTokens();
  const tokens = useMemo(() => ({
    ...getSVGDesignTokens(dt, {
      type: type || '',
      disabled: props.disabled
    }),
    ...props.theme?.(dt)
  }), [dt, type, props]);
  const currentLocale = useLanguage();
  const isRTL = useIsRTL();
  const [attrValue, setAttrRef] = useClosestAttribute('dir', [isRTL]);
  const getIconA11yInfo = useIconA11yInfo();
  const {
    ariaLabel,
    ariaHidden,
    tooltip
  } = getIconA11yInfo(props);
  const svgIcon = localizedIcon ? localizedIcon : icon;
  const iconId = `sas-svg-${svgIcon.name.toLowerCase()}`;
  const isPresentInCache = isSVGAvailableInCache(iconId);
  const forceRender = useForceUpdate();
  useEffect(() => {
    let cancelled = false;
    const importIcon = iconName => {
      const uppercaseIconName = iconName.charAt(0).toUpperCase() + iconName.slice(1);
      const importedIcon = findLocalizedSVG(uppercaseIconName);
      if (cancelled) return;
      return importedIcon;
    };
    const localizeIcon = () => {
      const localizedIconOverride = icon.localeOverrides[currentLocale.split('-')?.[0]];
      if (localizedIconOverride) {
        // Load the override icon
        const iconLocalized = localizedIconOverride.name ? importIcon(localizedIconOverride.name) : {
          ...icon
        };
        if (cancelled) {
          return;
        }

        // Update the suppressMirroring property
        if (iconLocalized) {
          iconLocalized.suppressMirroring = localizedIconOverride.suppressMirroring ?? icon.suppressMirroring;
          setLocalizedIcon(iconLocalized);
        } else {
          setLocalizedIcon(undefined);
        }
      } else {
        setLocalizedIcon(undefined);
      }
    };
    localizeIcon();
    return () => {
      cancelled = true;
    };
  }, [currentLocale, icon]);
  useEffect(() => {
    let ignoreUpdate = false;
    !isPresentInCache && updateSVG(svgIcon, {
      className: cx('sas-icon', cn.inner),
      id: iconId,
      spanClassName: cx('sas-icon', cn.svg, type === 'icon' && cn.icon, props.disabled && cn.disabled),
      spanStyleTokens: tokens,
      mirroringStyle: cx(!svgIcon.suppressMirroring && attrValue === 'rtl' && cn.allowMirroring)
    }, forceRender, ignoreUpdate);
    return () => {
      ignoreUpdate = true;
    };
  }, [svgIcon, iconId, isPresentInCache, attrValue, forceRender, props.disabled, tokens, type, isRTL]);

  // icon render
  const iconRender = getSVG({
    className: cx('sas-icon', cn.inner),
    id: iconId,
    spanClassName: cx('sas-icon', cn.svg, type === 'icon' && cn.icon, props.disabled && cn.disabled),
    spanStyleTokens: tokens,
    mirroringStyle: cx(!svgIcon.suppressMirroring && attrValue === 'rtl' && cn.allowMirroring)
  });
  useEffect(() => {
    const svgArea = document.getElementById('sas-svg-area');
    if (svgArea) {
      svgArea.setAttribute('data-sas-theme', dt.themeVariant);
    }
  }, [dt.themeVariant]);
  return /*#__PURE__*/_jsx("span", {
    ...restProps,
    ref: element => {
      setAttrRef(element);
      setRef(ref, element);
    },
    className: cx('sas-icon', cn.svg, type === 'icon' && cn.icon, props.disabled && cn.disabled, props.className),
    "data-testid": testId,
    style: {
      ...tokens,
      ...props.style
    }
    // aria-label should only be set if labelledby isn't
    ,
    "aria-label": props.ariaLabelledBy ? undefined : ariaLabel,
    role: role ?? 'img',
    "aria-hidden": ariaHidden,
    title: tooltip,
    "aria-labelledby": props.ariaLabelledBy,
    "aria-describedby": props.ariaDescribedBy,
    children: iconRender
  });
}));
export const getSVGDesignTokens = (dt, opts) => {
  const iconTokens = renderDesignTokens(getIconTokens(dt), 'icon');
  const thumbnailTokens = renderDesignTokens(getThumbnailTokens(dt), 'thumbnail');
  const tokens = {
    ...renderDesignTokens({
      opacity: opts?.disabled ? 0.3 : 1
    }, 'svg'),
    ...(opts?.type === 'icon' || opts?.type === '' ? iconTokens : {}),
    ...(opts?.type === 'svg' || opts?.type === '' ? {
      '--viya-logo': dt.themeVariant === 'light' ? '#032954' : '#fff'
    } : {}),
    ...(opts?.type === 'pnp' || opts?.type === '' ? renderDesignTokens(getPNPTokens(dt), 'pnp') : {}),
    ...(opts?.type === 'boss' || opts?.type === '' ? {
      ...renderDesignTokens(getPNPTokens(dt), 'pnp'),
      ...renderDesignTokens(getBOSSTokens(dt), 'boss')
    } : {}),
    ...(opts?.type === 'help' || opts?.type === '' ? renderDesignTokens(getHelpTokens(dt), 'help') : {}),
    ...(opts?.type === 'zerostate' || opts?.type === '' ? renderDesignTokens(getZeroStateTokens(dt), 'zerostate') : {}),
    ...(opts?.type === 'thumbnail' || opts?.type === '' ? thumbnailTokens : {}),
    ...(opts?.type === 'eg-studio' || opts?.type === '' ? renderDesignTokens(getEgStudioTokens(dt), 'eg-studio') : {}),
    ...(opts?.type === 'eg-studio' || opts?.type === '' ? getUnprefixedEgStudioTokens(dt) : {}),
    ...(opts?.type === 'report' || opts?.type === '' ? getReportTokens(dt) : {}),
    ...(opts?.type === 'vendor' || opts?.type === 'icon' || opts?.type === '' ? renderDesignTokens(getVendorTokens(dt), 'vendor') : {}),
    ...(opts?.type === 'vendor' || opts?.type === 'icon' || opts?.type === '' ? {
      '--icon-default-fg': iconTokens['--icon-default-fg'],
      '--thumbnail-backgroundfill': thumbnailTokens['--thumbnail-backgroundfill'],
      '--thumbnail-sas-logo': thumbnailTokens['--thumbnail-sas-logo'],
      '--thumbnail-slack-background': thumbnailTokens['--thumbnail-slack-background']
    } : {})
  };

  // Includes all tokens if no type is set
  return tokens;
};
const getIconTokens = dt => {
  const tokens = {
    'default-fg': dt.tokens['color-text-neutral'],
    'neutral-fg': dt.tokens['color-status-icon-neutral'],
    'alert-fg': dt.tokens['color-status-icon-alert'],
    'warning-fg': dt.tokens['color-status-icon-warning'],
    'success-fg': dt.tokens['color-status-icon-success'],
    'information-fg': dt.tokens['color-status-icon-info'],
    'nodeStatus-bg': setAlpha(dt.tokens['color-text-neutral-inverse'], 0.8)
  };
  switch (dt.themeVariant) {
    case 'high-contrast':
    case 'dark':
      return {
        ...tokens,
        'neutralfill-fg': setAlpha(dt.tokens['color-status-icon-neutral'], 0.6)
      };
    default:
      return {
        ...tokens,
        'neutralfill-fg': setAlpha(dt.tokens['color-status-icon-neutral'], 0.5)
      };
  }
};
const getHelpTokens = dt => {
  if (dt.themeVariant === 'high-contrast') {
    return {
      'color1-fill': dt.tokens['color-dataviz-graph7']
    };
  }
  return {
    'color1-fill': dt.tokens['color-primary']
  };
};
const getZeroStateTokens = dt => {
  switch (dt.themeVariant) {
    case 'high-contrast':
      return {
        zscolor1: dt.tokens['color-secondary'],
        'zscolor1-opacity': '0.4',
        'zscolor1-dots-opacity': '0.5',
        zscolor2: dt.tokens['color-neutral'],
        'zscolor2-opacity1': '0.6',
        'zscolor2-opacity2': '0.3',
        'base-fill': dt.tokens['color-zero-state-base-fill-1'],
        'base-border': dt.tokens['color-zero-state-base-fill-2'],
        'modifier-fill': dt.tokens['color-zero-state-modifier-fill-1'],
        'modifier-fill-alt1': dt.tokens['color-zero-state-base-fill-1'],
        'modifier-fill-alt2': dt.tokens['color-zero-state-modifier-fill-3'],
        'modifier-fill-alt3': dt.tokens['color-zero-state-modifier-fill-4'],
        'modifier-fill-alt4': dt.tokens['color-zero-state-modifier-fill-5'],
        'modifier-border': dt.tokens['color-zero-state-modifier-fill-2'],
        'small-base-fill': dt.tokens['color-zero-state-base-fill-1'],
        'small-base-border': dt.tokens['color-zero-state-base-fill-3'],
        'small-base-modifier': dt.tokens['color-zero-state-modifier-fill-1'],
        'small-modifier-fill-alt1': dt.tokens['color-zero-state-base-fill-1'],
        'small-modifier-fill-alt2': dt.tokens['color-zero-state-modifier-fill-4'],
        'small-modifier-border': dt.tokens['color-zero-state-base-fill-2']
      };
    case 'dark':
      return {
        zscolor1: dt.tokens['color-neutral'],
        'zscolor1-opacity': '0.3',
        'zscolor1-dots-opacity': '0.25',
        zscolor2: dt.tokens['color-neutral'],
        'zscolor2-opacity1': '0.6',
        'zscolor2-opacity2': '0.3',
        'base-fill': dt.tokens['color-zero-state-base-fill-1'],
        'base-border': dt.tokens['color-zero-state-base-fill-2'],
        'modifier-fill': dt.tokens['color-zero-state-modifier-fill-1'],
        'modifier-fill-alt1': dt.tokens['color-zero-state-base-fill-1'],
        'modifier-fill-alt2': dt.tokens['color-zero-state-modifier-fill-3'],
        'modifier-fill-alt3': dt.tokens['color-zero-state-modifier-fill-4'],
        'modifier-fill-alt4': dt.tokens['color-zero-state-modifier-fill-5'],
        'modifier-border': dt.tokens['color-zero-state-modifier-fill-2'],
        'small-base-fill': dt.tokens['color-zero-state-base-fill-1'],
        'small-base-border': dt.tokens['color-zero-state-base-fill-3'],
        'small-base-modifier': dt.tokens['color-zero-state-modifier-fill-1'],
        'small-modifier-fill-alt1': dt.tokens['color-zero-state-base-fill-1'],
        'small-modifier-fill-alt2': dt.tokens['color-zero-state-modifier-fill-4'],
        'small-modifier-border': dt.tokens['color-zero-state-base-fill-2']
      };
    default:
      return {
        zscolor1: dt.tokens['color-secondary'],
        'zscolor1-opacity': '0.1',
        'zscolor1-dots-opacity': '0.25',
        zscolor2: dt.tokens['color-neutral'],
        'zscolor2-opacity1': '0.8',
        'zscolor2-opacity2': '0.4',
        'base-fill': dt.tokens['color-zero-state-base-fill-1'],
        'base-border': dt.tokens['color-zero-state-base-fill-2'],
        'modifier-fill': dt.tokens['color-zero-state-modifier-fill-1'],
        'modifier-fill-alt1': dt.tokens['color-zero-state-base-fill-1'],
        'modifier-fill-alt2': dt.tokens['color-zero-state-modifier-fill-3'],
        'modifier-fill-alt3': dt.tokens['color-zero-state-modifier-fill-4'],
        'modifier-fill-alt4': dt.tokens['color-zero-state-modifier-fill-5'],
        'modifier-border': dt.tokens['color-zero-state-modifier-fill-2'],
        'small-base-fill': dt.tokens['color-zero-state-base-fill-1'],
        'small-base-border': dt.tokens['color-zero-state-base-fill-3'],
        'small-base-modifier': dt.tokens['color-zero-state-modifier-fill-1'],
        'small-modifier-fill-alt1': dt.tokens['color-zero-state-base-fill-1'],
        'small-modifier-fill-alt2': dt.tokens['color-zero-state-modifier-fill-4'],
        'small-modifier-border': dt.tokens['color-zero-state-base-fill-2']
      };
  }
};
const getThumbnailTokens = dt => {
  switch (dt.themeVariant) {
    case 'high-contrast':
      return {
        backgroundfill: dt.tokens['color-container-background-0'],
        overlay: dt.tokens['color-container-background-0'],
        shortcut: dt.tokens['color-text-neutral'],
        color1: dt.tokens['color-dataviz-graph1'],
        color2: dt.tokens['color-dataviz-graph5'],
        color3: dt.tokens['color-dataviz-graph4'],
        color4: dt.tokens['color-dataviz-graph3'],
        'slack-background': '#000000',
        'jira-background': '#000000',
        'facebook-logo': '#FFFFFF',
        'sas-logo': '#FFFFFF',
        'fill-sas': dt.tokens['color-thumbnail-fill-4'],
        'fill-dark': dt.tokens['color-thumbnail-fill-3'],
        'fill-med': dt.tokens['color-thumbnail-fill-2'],
        'fill-light': dt.tokens['color-thumbnail-fill-1'],
        'fill-overlay': dt.tokens['color-container-background-0']
      };
    case 'dark':
      return {
        backgroundfill: dt.tokens['color-container-background-0'],
        overlay: dt.tokens['color-container-background-0'],
        shortcut: dt.tokens['color-text-neutral'],
        color1: dt.tokens['color-dataviz-graph1'],
        color2: dt.tokens['color-dataviz-graph5'],
        color3: dt.tokens['color-dataviz-graph4'],
        color4: dt.tokens['color-dataviz-graph3'],
        'slack-background': '#4A154B',
        'jira-background': '#0D56C9',
        'facebook-logo': '#FFFFFF',
        'sas-logo': '#FFFFFF',
        'fill-sas': dt.tokens['color-thumbnail-fill-4'],
        'fill-dark': dt.tokens['color-thumbnail-fill-3'],
        'fill-med': dt.tokens['color-thumbnail-fill-2'],
        'fill-light': dt.tokens['color-thumbnail-fill-1'],
        'fill-overlay': dt.tokens['color-container-background-0']
      };
    default:
      return {
        backgroundfill: dt.tokens['color-container-background-0'],
        overlay: dt.tokens['color-container-background-0'],
        shortcut: dt.tokens['color-text-neutral'],
        color1: dt.tokens['color-dataviz-graph1'],
        color2: dt.tokens['color-dataviz-graph5'],
        color3: dt.tokens['color-dataviz-graph4'],
        color4: dt.tokens['color-dataviz-graph3'],
        'slack-background': '#36C5F0',
        'jira-background': '#0D56C9',
        'facebook-logo': '#1877F2',
        'sas-logo': '#012036',
        'fill-sas': dt.tokens['color-thumbnail-fill-4'],
        'fill-dark': dt.tokens['color-thumbnail-fill-3'],
        'fill-med': dt.tokens['color-thumbnail-fill-2'],
        'fill-light': dt.tokens['color-thumbnail-fill-1'],
        'fill-overlay': dt.tokens['color-container-background-0']
      };
  }
};
const getEgStudioTokens = dt => {
  switch (dt.themeVariant) {
    case 'high-contrast':
      return {
        bg: dt.tokens['color-container-border-4'],
        'bg-white': dt.tokens['color-container-background-0'],
        codedata: dt.tokens['color-text-primary'],
        codetext: dt.tokens['color-neutral'],
        codeeditormarkers: dt.tokens['color-text-neutral'],
        codemarker: dt.tokens['color-text-neutral'],
        codenumber: '#28E6D1',
        codeproc: '#ACD8F9',
        codebgnotab: dt.tokens['color-container-background-0'],
        'codebgnotab-stroke': dt.tokens['color-text-neutral'],
        codebg: dt.tokens['color-container-background-2'],
        'codebg-stroke': dt.tokens['color-container-border-4'],
        processarrows: dt.tokens['color-container-border-4'],
        processflows: dt.tokens['color-container-border-4'],
        'processflows-o25': 0.25,
        'processflows-o50': 0.5,
        'processflows-o75': 0.75,
        processflowarrows: dt.tokens['color-container-border-4'],
        nodecolumn: '#73CCFF',
        nodetransform: '#FFF93E',
        nodedata: dt.tokens['color-neutral'],
        modelfitbg: dt.tokens['color-container-background-0'],
        'modelfitbg-stroke': '#FFFFFF',
        nocurve: dt.tokens['color-text-neutral'],
        highthreshold: dt.tokens['color-status-alert'],
        medthreshold: dt.tokens['color-status-warning'],
        lowthreshold: dt.tokens['color-status-info'],
        columnfill: dt.tokens['color-container-background-0'],
        columnborder: dt.tokens['color-container-border-4'],
        columnmap: dt.tokens['color-label-text'],
        maparrow: dt.tokens['color-label-text'],
        columninfo: dt.tokens['color-label-text'],
        'columninfo-opacity': 0.6,
        columnbordermissing: dt.tokens['color-label-text'],
        'columnbordermissing-opacity': 1,
        columnmapmissing: dt.tokens['color-label-text'],
        'columnmapmissing-opacity': 1,
        columninfomissing: dt.tokens['color-label-text'],
        'columninfomissing-opacity': 1,
        columnwarning: dt.tokens['color-status-warning'],
        'columnwarning-stroke': '#000000',
        columnmapwarning: dt.tokens['color-status-warning'],
        columninfowarning: dt.tokens['color-status-warning'],
        'columninfowarning-opacity': 0.5,
        columnalert: dt.tokens['color-status-alert'],
        'columnalert-stroke': '#000000',
        columnmapalert: dt.tokens['color-status-alert'],
        columninfoalert: dt.tokens['color-status-alert'],
        'columninfoalert-opacity': 1,
        'bg-fill': dt.tokens['color-container-background-2'],
        'bg-border': dt.tokens['color-container-border-4']
      };
    case 'dark':
      return {
        bg: dt.tokens['color-container-border-4'],
        'bg-white': dt.tokens['color-container-background-0'],
        codedata: dt.tokens['color-text-primary'],
        codetext: dt.tokens['color-neutral'],
        codeeditormarkers: dt.tokens['color-text-neutral'],
        codemarker: dt.tokens['color-text-neutral'],
        codenumber: '#0CACA4',
        codeproc: '#8F8FFF',
        codebgnotab: dt.tokens['color-container-background-0'],
        'codebgnotab-stroke': 'none',
        codebg: dt.tokens['color-container-background-2'],
        'codebg-stroke': 'none',
        processarrows: dt.tokens['color-container-border-4'],
        processflows: dt.tokens['color-container-border-4'],
        'processflows-o25': 0.25,
        'processflows-o50': 0.5,
        'processflows-o75': 0.75,
        processflowarrows: dt.tokens['color-container-border-4'],
        nodecolumn: '#35A5FF',
        nodetransform: '#EBCF5C',
        nodedata: dt.tokens['color-neutral'],
        modelfitbg: dt.tokens['color-container-background-0'],
        'modelfitbg-stroke': 'none',
        nocurve: dt.tokens['color-text-neutral'],
        highthreshold: dt.tokens['color-status-alert'],
        medthreshold: dt.tokens['color-status-warning'],
        lowthreshold: dt.tokens['color-status-info'],
        columnfill: dt.tokens['color-container-background-0'],
        columnborder: dt.tokens['color-container-border-4'],
        columnmap: dt.tokens['color-label-text'],
        maparrow: dt.tokens['color-label-text'],
        columninfo: dt.tokens['color-label-text'],
        'columninfo-opacity': 0.3,
        columnbordermissing: dt.tokens['color-label-text'],
        'columnbordermissing-opacity': 0.5,
        columnmapmissing: dt.tokens['color-label-text'],
        'columnmapmissing-opacity': 0.5,
        columninfomissing: dt.tokens['color-label-text'],
        'columninfomissing-opacity': 0.3,
        columnwarning: dt.tokens['color-status-warning'],
        'columnwarning-stroke': 'none',
        columnmapwarning: dt.tokens['color-status-warning'],
        columninfowarning: dt.tokens['color-status-warning'],
        'columninfowarning-opacity': 0.3,
        columnalert: dt.tokens['color-status-alert'],
        'columnalert-stroke': 'none',
        columnmapalert: dt.tokens['color-status-alert'],
        columninfoalert: dt.tokens['color-status-alert'],
        'columninfoalert-opacity': 0.3,
        'bg-fill': dt.tokens['color-container-background-2'],
        'bg-border': dt.tokens['color-container-border-4']
      };
    default:
      return {
        bg: dt.tokens['color-container-border-4'],
        'bg-white': dt.tokens['color-container-background-0'],
        codedata: dt.tokens['color-text-primary'],
        codetext: dt.tokens['color-neutral'],
        codeeditormarkers: dt.tokens['color-text-neutral'],
        codemarker: dt.tokens['color-text-neutral'],
        codenumber: '#08726D',
        codeproc: '#000080',
        codebgnotab: dt.tokens['color-container-background-0'],
        'codebgnotab-stroke': 'none',
        codebg: dt.tokens['color-container-background-2'],
        'codebg-stroke': 'none',
        processarrows: dt.tokens['color-container-border-4'],
        processflows: dt.tokens['color-container-border-4'],
        'processflows-o25': 0.25,
        'processflows-o50': 0.5,
        'processflows-o75': 0.75,
        processflowarrows: dt.tokens['color-container-border-4'],
        nodecolumn: '#277FC7',
        nodetransform: '#C79F27',
        nodedata: dt.tokens['color-neutral'],
        modelfitbg: dt.tokens['color-container-background-2'],
        'modelfitbg-stroke': 'none',
        nocurve: dt.tokens['color-text-neutral'],
        highthreshold: dt.tokens['color-status-alert'],
        medthreshold: dt.tokens['color-status-warning'],
        lowthreshold: dt.tokens['color-status-info'],
        columnfill: dt.tokens['color-container-background-0'],
        columnborder: dt.tokens['color-container-border-4'],
        columnmap: dt.tokens['color-label-text'],
        maparrow: dt.tokens['color-label-text'],
        columninfo: dt.tokens['color-label-text'],
        'columninfo-opacity': 0.3,
        columnbordermissing: dt.tokens['color-label-text'],
        'columnbordermissing-opacity': 0.5,
        columnmapmissing: dt.tokens['color-label-text'],
        'columnmapmissing-opacity': 0.5,
        columninfomissing: dt.tokens['color-label-text'],
        'columninfomissing-opacity': 0.3,
        columnwarning: dt.tokens['color-status-warning'],
        'columnwarning-stroke': 'none',
        columnmapwarning: dt.tokens['color-status-warning'],
        columninfowarning: dt.tokens['color-status-warning'],
        'columninfowarning-opacity': 0.3,
        columnalert: dt.tokens['color-status-alert'],
        'columnalert-stroke': 'none',
        columnmapalert: dt.tokens['color-status-alert'],
        columninfoalert: dt.tokens['color-status-alert'],
        'columninfoalert-opacity': 0.3,
        'bg-fill': dt.tokens['color-container-background-2'],
        'bg-border': dt.tokens['color-container-border-4']
      };
  }
};
const getUnprefixedEgStudioTokens = dt => {
  switch (dt.themeVariant) {
    case 'high-contrast':
      return {
        '--health-content': dt.tokens['color-icon-primary'],
        '--rx': dt.tokens['color-dataviz-graph4'],
        '--stethoscope': dt.tokens['color-dataviz-graph1'],
        '--decision-diagram': dt.tokens['color-icon-primary'],
        '--settings-content': dt.tokens['color-dataviz-graph1']
      };
    case 'dark':
      return {
        '--health-content': dt.tokens['color-icon-primary'],
        '--rx': dt.reportTokens.c.colorGraphAlt4,
        '--stethoscope': dt.reportTokens.c.colorGraphAlt1,
        '--decision-diagram': dt.tokens['color-icon-primary'],
        '--settings-content': dt.reportTokens.c.colorGraphAlt1
      };
    default:
      return {
        '--health-content': dt.tokens['color-icon-primary'],
        '--rx': dt.tokens['color-dataviz-graph4'],
        '--stethoscope': dt.tokens['color-dataviz-graph1'],
        '--decision-diagram': dt.tokens['color-icon-primary'],
        '--settings-content': dt.tokens['color-dataviz-graph1']
      };
  }
};
const getPNPTokens = dt => {
  return {
    'layout-tiles': dt.tokens['color-container-background-0'],
    'layout-border': dt.tokens['color-container-border-4'],
    'layout-background': dt.tokens['color-container-background-2']
  };
};
const getBOSSTokens = dt => {
  return {
    'layout-fill': dt.tokens['color-container-background-1']
  };
};
const getVendorTokens = dt => {
  switch (dt.themeVariant) {
    case 'high-contrast':
      return {
        adformTypeLarge: '#fff',
        adformBlueLarge: '#fff',
        adformGreenLarge: '#fff',
        adformYellowLarge: '#fff',
        adformBlue: '#fff',
        adformGreen: '#fff',
        adformYellow: '#fff',
        clouderaLogo: '#fff',
        facebookBlue: '#fff',
        f: dt.tokens['color-container-background-0'],
        thumbFacebookLogo: '#fff',
        ibmLogo: '#fff',
        impalaLogo: '#fff',
        instagramLogo: '#fff',
        kafkaLogo: '#fff',
        linkedInLogo: '#fff',
        mariaDBLogo: '#fff',
        mongoLogo: '#fff',
        mongoLeaf: '#00ed64',
        mySQLTeal: dt.tokens['color-container-background-0'],
        mySQLTealLarge: '#fff',
        mySQLOrange: '#fff',
        oracleRed: '#fff',
        oracleDB: '#fff',
        sasLogo: '#fff',
        singleStoreText: '#fff',
        slackText: '#fff',
        slackBlue: '#36c5f0',
        slackGreen: '#2eb67d',
        slackRed: '#e01e5a',
        slackYellow: '#ecb22e',
        ghostOutline: '#fff',
        ghostFill: '#000',
        snowflakeLogo: '#fff',
        sparkText: '#fff',
        sparkStar: '#e26a1a',
        syniverseColor: '#0BF4CD',
        syniverseText: '#fff',
        teradataText: '#fff',
        teradataDot: '#fff',
        twitterBlue: '#fff',
        youTubeFrame: '#fff',
        youTubePlay: '#000',
        youTubeText: '#fff',
        primary: dt.tokens['color-primary'],
        primaryOp: '40%',
        xFill: '#fff'
      };
    case 'dark':
      return {
        adformTypeLarge: '#fff',
        adformBlueLarge: '#fff',
        adformGreenLarge: '#fff',
        adformYellowLarge: '#fff',
        adformBlue: '#0981bd',
        adformGreen: '#60c1a4',
        adformYellow: '#ebd769',
        clouderaLogo: '#fff',
        facebookBlue: '#fff',
        f: dt.tokens['color-container-background-0'],
        thumbFacebookLogo: '#fff',
        ibmLogo: '#fff',
        impalaLogo: '#fff',
        instagramLogo: '#fff',
        kafkaLogo: '#fff',
        linkedInLogo: '#fff',
        mariaDBLogo: '#fff',
        mongoLogo: '#fff',
        mongoLeaf: '#00ed64',
        mySQLTeal: '#00758f',
        mySQLTealLarge: '#fff',
        mySQLOrange: '#fff',
        oracleRed: '#fff',
        oracleDB: '#fff',
        sasLogo: '#fff',
        singleStoreText: '#fff',
        slackText: '#fff',
        slackBlue: '#fff',
        slackGreen: '#fff',
        slackRed: '#fff',
        slackYellow: '#fff',
        ghostOutline: '#fff',
        ghostFill: '#000',
        snowflakeLogo: '#fff',
        sparkText: '#fff',
        sparkStar: '#e26a1a',
        syniverseColor: '#0BF4CD',
        syniverseText: '#fff',
        teradataText: '#fff',
        teradataDot: '#fff',
        twitterBlue: '#1d9bf0',
        youTubeFrame: '#ff0000',
        youTubePlay: '#fff',
        youTubeText: '#fff',
        primary: dt.tokens['color-primary'],
        primaryOp: '70%',
        xFill: '#fff'
      };
    default:
      return {
        adformTypeLarge: '#737374',
        adformBlueLarge: '#0981bd',
        adformGreenLarge: '#60c1a4',
        adformYellowLarge: '#ebd769',
        adformBlue: '#0981bd',
        adformGreen: '#60c1a4',
        adformYellow: '#ebd769',
        clouderaLogo: '#f26622',
        facebookBlue: '#1877F2',
        f: '#fff',
        thumbFacebookLogo: '#1877F2',
        ibmLogo: '#000000',
        impalaLogo: '#266792',
        instagramLogo: '#000',
        kafkaLogo: '#231f20',
        linkedInLogo: '#0a66c2',
        mariaDBLogo: '#003545',
        mongoLogo: '#00684a',
        mongoLeaf: '#00684a',
        mySQLTeal: '#00758f',
        mySQLTealLarge: '#00758f',
        mySQLOrange: '#f29111',
        oracleRed: '#e30613',
        oracleDB: '#000',
        sasLogo: '#0766d1',
        singleStoreText: '#000',
        slackText: '#000',
        slackBlue: '#36c5f0',
        slackGreen: '#2eb67d',
        slackRed: '#e01e5a',
        slackYellow: '#ecb22e',
        ghostOutline: '#000',
        ghostFill: '#fff',
        snowflakeLogo: '#2bb5e9',
        sparkText: '#424143',
        sparkStar: '#e26a1a',
        syniverseColor: '#0BF4CD',
        syniverseText: '#000',
        teradataText: '#3d4952',
        teradataDot: '#ff6600',
        twitterBlue: '#1d9bf0',
        youTubeFrame: '#ff0000',
        youTubePlay: '#fff',
        youTubeText: '#282828',
        primary: dt.tokens['color-primary'],
        primaryOp: '10%',
        xFill: '#000000'
      };
  }
};
const getReportTokens = dt => {
  return {
    '--report-neutral-color': dt.tokens['color-neutral'],
    '--report-theme-background': dt.tokens['color-container-background-0'],
    '--report-theme-primary': dt.tokens['color-primary'],
    '--report-text-color': dt.tokens['color-text-neutral'],
    '--report-background-color': dt.tokens['color-container-background-0'],
    '--data-color-one': dt.tokens['color-dataviz-graph1'],
    '--data-color-two': dt.tokens['color-dataviz-graph2'],
    '--data-color-three': dt.tokens['color-dataviz-graph3'],
    '--data-color-four': dt.tokens['color-dataviz-graph4'],
    '--data-color-five': dt.tokens['color-dataviz-graph5'],
    '--data-color-six': dt.tokens['color-dataviz-graph6'],
    '--data-color-seven': dt.tokens['color-dataviz-graph7'],
    '--data-color-eight': dt.tokens['color-dataviz-graph8']
  };
};
//# sourceMappingURL=SVG.js.map